// FeelingDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Paper, Typography, Container, TextField, Button } from '@mui/material';
import excuseApi from '../../api/excuseApi';
import Comment from '../../models/Comment';

const getAvatarUrl = (obj) => {
  if (!obj) return `https://ui-avatars.com/api/?name=Anon&background=random`;
  if (obj.avatar) return `${process.env.REACT_APP_STATIC_URL}${obj.avatar}`;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(obj.username || 'Anon')}&background=random`;
};

const FeelingDetail = () => {
  const { id } = useParams();
  const [feeling, setFeeling] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [replyText, setReplyText] = useState({});
  const [subVisibleCount, setSubVisibleCount] = useState({});

  // Load current user
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      import('../../models/Account').then(({ getCurrentUser }) => {
        getCurrentUser().then(u => setUser(u));
      });
    }
  }, []);

  const loadFeeling = async () => {
    setLoading(true);
    try {
      const resFeeling = await excuseApi({ endpoint: `/feelings/${id}`, method: 'GET' });
      setFeeling(resFeeling);

      const resComments = await excuseApi({ endpoint: `/comments?target_type=feeling&target_id=${id}`, method: 'GET' });
      // Initialize subcomments
      const withExtras = (resComments.items || []).map(c => ({ ...c, subcomments: [], subLoaded: false }));
      setComments(withExtras);
      // Load subcomments
      withExtras.forEach(c => loadSubcomments(c.id));
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeeling();
  }, [id]);

  const loadSubcomments = async (parentId) => {
    try {
      const res = await excuseApi({ endpoint: `/comments/${parentId}/subcomments`, method: 'GET' });
      setComments(prev =>
        prev.map(c => c.id === parentId ? { ...c, subcomments: res || [], subLoaded: true } : c)
      );
      setSubVisibleCount(prev => ({ ...prev, [parentId]: 5 }));
    } catch (err) {
      throw err;
    }
  };

  const hasReplied = (parentId) => {
    if (!user) return false;
    const comment = comments.find(c => c.id === parentId);
    if (!comment || !comment.subcomments) return false;
    return comment.subcomments.some(sub => sub.account_id === user.id);
  };

  const handleNewCommentSubmit = async () => {
    if (!user || !newCommentText.trim()) return;
    try {
      await excuseApi({
        endpoint: '/comments',
        method: 'POST',
        data: {
          target_type: 'feeling',
          target_id: id,
          account_id: user.id,
          content: newCommentText.trim(),
        },
      });
      setNewCommentText('');
      loadFeeling();
    } catch (err) {
      throw err;
    }
  };

  const handleReplySubmit = async (parentId) => {
    if (!user || !replyText[parentId]?.trim()) return;
    try {
      await Comment.createComment({
        target_type: 'comment',
        target_id: parentId,
        parent_id: parentId,
        account_id: user.id,
        content: replyText[parentId].trim(),
      });
      setReplyText(prev => ({ ...prev, [parentId]: '' }));
      loadSubcomments(parentId);
    } catch (err) {
      throw err;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center">Đang tải...</Typography>
      </Container>
    );
  }

  if (!feeling) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center">Cảm nhận không tồn tại</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#fbf7e4', borderRadius: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box
            component="img"
            src={getAvatarUrl(feeling)}
            alt={feeling.username || 'Ẩn danh'}
            sx={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover' }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">{feeling.username || 'Ẩn danh'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Intl.DateTimeFormat('vi-VN', {
                timeZone: 'Asia/Ho_Chi_Minh',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }).format(new Date(feeling.created_at))}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ bgcolor: '#fffbe6', p: 2, borderRadius: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div dangerouslySetInnerHTML={{ __html: feeling.content }} style={{ color: '#4a5933', fontSize: '1rem', lineHeight: 1.6 }} />
        </Box>
      </Paper>

      {/* Comment section */}
      <Box>
        <Typography variant="h6" mb={2}>Bình luận</Typography>

        {user && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Viết bình luận..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              sx={{ bgcolor: '#fff', borderRadius: 1, border: '1px solid #8a0000', mb: 1 }}
            />
            <Button variant="contained" sx={{ bgcolor: '#8a0000', color: '#fbf7e4' }} onClick={handleNewCommentSubmit}>
              Gửi bình luận
            </Button>
          </Box>
        )}

        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Chưa có bình luận nào.</Typography>
        ) : (
          comments.map(comment => (
            <Paper key={comment.id} sx={{ p: 2, mb: 2, bgcolor: '#f4f4f4', borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Box
                  component="img"
                  src={getAvatarUrl(comment)}
                  alt={comment.username || 'Ẩn danh'}
                  sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                />
                <Typography variant="subtitle2" fontWeight="bold">{comment.username || 'Ẩn danh'}</Typography>
                <Typography variant="caption" color="text.secondary" ml={1}>
                  {new Intl.DateTimeFormat('vi-VN', {
                    timeZone: 'Asia/Ho_Chi_Minh',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).format(new Date(comment.created_at))}
                </Typography>
              </Box>
              <Typography variant="body2">{comment.content}</Typography>

              {/* Subcomments */}
              {comment.subLoaded && comment.subcomments.slice(0, subVisibleCount[comment.id] || 5).map(sub => (
                <Paper key={sub.id} sx={{ p: 1, mb: 1, bgcolor: '#fffbe6', borderRadius: 1, ml: 4 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box component="img" src={getAvatarUrl(sub)} alt={sub.username || 'Ẩn danh'} sx={{ width: 25, height: 25, borderRadius: '50%', objectFit: 'cover' }} />
                    <Typography variant="subtitle2" fontWeight="bold">{sub.username || 'Ẩn danh'}</Typography>
                  </Box>
                  <Typography variant="body2">{sub.content}</Typography>
                </Paper>
              ))}

              {/* Reply form */}
              {user && !hasReplied(comment.id) && (
                <Box sx={{ mt: 1, ml: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Viết phản hồi..."
                    value={replyText[comment.id] || ''}
                    onChange={(e) => setReplyText(prev => ({ ...prev, [comment.id]: e.target.value }))}
                    sx={{ bgcolor: '#fff', borderRadius: 1, border: '1px solid #8a0000', mb: 1 }}
                  />
                  <Button variant="contained" sx={{ bgcolor: '#8a0000', color: '#fbf7e4' }} onClick={() => handleReplySubmit(comment.id)}>
                    Gửi phản hồi
                  </Button>
                </Box>
              )}
            </Paper>
          ))
        )}
      </Box>
    </Container>
  );
};

export default FeelingDetail;
